import { useState } from "react";
import type {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormUnregister,
  UseFormSetValue,
} from "react-hook-form";
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FormField, Input } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { UpdateProfileData } from "../../types";

interface PasswordSectionProps {
  register: UseFormRegister<UpdateProfileData>;
  errors: FieldErrors<UpdateProfileData>;
  watch: UseFormWatch<UpdateProfileData>;
  unregister: UseFormUnregister<UpdateProfileData>;
  setValue: UseFormSetValue<UpdateProfileData>;
  isChangingPassword: boolean;
  setIsChangingPassword: (value: boolean) => void;
}

export function PasswordSection({
  register,
  errors,
  watch,
  unregister,
  setValue,
  isChangingPassword,
  setIsChangingPassword,
}: PasswordSectionProps) {
  const { t } = useTranslation("users");

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const newPassword = watch("newPassword");

  const strengthLevels = [
    t("password.strength.levels.veryWeak"),
    t("password.strength.levels.weak"),
    t("password.strength.levels.fair"),
    t("password.strength.levels.good"),
    t("password.strength.levels.strong"),
  ];

  const getPasswordStrength = (password: string) => {
    if (!password) return { strength: 0, label: "", color: "" };

    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[@$!%*?&]/.test(password)) strength++;

    const colors = [
      "bg-red-500",
      "bg-orange-500",
      "bg-yellow-500",
      "bg-blue-500",
      "bg-green-500",
    ];

    return {
      strength,
      label: strengthLevels[strength - 1] || "",
      color: colors[strength - 1] || "bg-gray-300",
    };
  };

  const passwordStrength = getPasswordStrength(newPassword || "");

  const passwordRequirements = [
    {
      test: (pwd: string) => pwd.length >= 8,
      label: t("password.requirements.minLength"),
    },
    {
      test: (pwd: string) => /[a-z]/.test(pwd),
      label: t("password.requirements.lowercase"),
    },
    {
      test: (pwd: string) => /[A-Z]/.test(pwd),
      label: t("password.requirements.uppercase"),
    },
    {
      test: (pwd: string) => /\d/.test(pwd),
      label: t("password.requirements.number"),
    },
    {
      test: (pwd: string) => /[@$!%*?&]/.test(pwd),
      label: t("password.requirements.special"),
    },
  ];

  const handleCancelPasswordChange = () => {
    setValue("currentPassword", "");
    setValue("newPassword", "");
    setValue("confirmNewPassword", "");

    unregister("currentPassword");
    unregister("newPassword");
    unregister("confirmNewPassword");

    setIsChangingPassword(false);
  };

  const renderPasswordInput = (
    fieldName: "currentPassword" | "newPassword" | "confirmNewPassword",
    show: boolean,
    setShow: (v: boolean) => void,
    placeholder: string,
    error: boolean,
    disabled = false,
  ) => (
    <div className="relative">
      <Lock className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
      <Input
        {...register(fieldName)}
        type={show ? "text" : "password"}
        size="sm"
        error={error}
        placeholder={placeholder}
        className="pl-8 pr-8"
        disabled={disabled}
      />
      <button
        type="button"
        onClick={() => setShow(!show)}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        disabled={disabled}
      >
        {show ? (
          <EyeOff className="h-3.5 w-3.5" />
        ) : (
          <Eye className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );

  return (
    <SectionCard
      icon={Lock}
      title={t("password.sectionTitle")}
      actions={
        <button
          type="button"
          onClick={() => {
            if (isChangingPassword) {
              handleCancelPasswordChange();
            } else {
              setIsChangingPassword(true);
            }
          }}
          className="text-[12px] text-gray-600 hover:text-gray-900 font-medium"
        >
          {isChangingPassword
            ? t("password.toggleCancel")
            : t("password.toggleChange")}
        </button>
      }
    >
      {!isChangingPassword ? (
        <p className="text-[12px] text-gray-500">{t("password.collapsedHint")}</p>
      ) : (
        <div className="space-y-1">
          <FormField
            layout="inline"
            size="sm"
            label={t("password.fields.currentPassword")}
            error={errors.currentPassword?.message}
            required
          >
            {renderPasswordInput(
              "currentPassword",
              showCurrentPassword,
              setShowCurrentPassword,
              t("password.placeholders.currentPassword"),
              !!errors.currentPassword,
            )}
          </FormField>

          <FormField
            layout="inline"
            size="sm"
            label={t("password.fields.newPassword")}
            error={errors.newPassword?.message}
            required
          >
            {renderPasswordInput(
              "newPassword",
              showNewPassword,
              setShowNewPassword,
              t("password.placeholders.newPassword"),
              !!errors.newPassword,
            )}
          </FormField>

          {newPassword && (
            <div className="pl-[8.5rem] flex items-center gap-2 -mt-0.5">
              <div className="flex-1 max-w-[200px] bg-gray-200 rounded-full h-1">
                <div
                  className={`h-1 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                  style={{
                    width: `${(passwordStrength.strength / 5) * 100}%`,
                  }}
                />
              </div>
              <span
                className={`text-[11px] font-medium ${
                  passwordStrength.strength >= 4
                    ? "text-green-600"
                    : passwordStrength.strength >= 3
                      ? "text-blue-600"
                      : passwordStrength.strength >= 2
                        ? "text-yellow-600"
                        : "text-red-600"
                }`}
              >
                {passwordStrength.label}
              </span>
            </div>
          )}

          <FormField
            layout="inline"
            size="sm"
            label={t("password.fields.confirmNewPassword")}
            error={errors.confirmNewPassword?.message}
            required
          >
            {renderPasswordInput(
              "confirmNewPassword",
              showConfirmPassword,
              setShowConfirmPassword,
              t("password.placeholders.confirmNewPassword"),
              !!errors.confirmNewPassword,
              !newPassword,
            )}
          </FormField>

          {newPassword && (
            <div className="pl-[8.5rem] mt-1.5">
              <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                {passwordRequirements.map((req, index) => {
                  const isValid = req.test(newPassword);
                  return (
                    <div key={index} className="flex items-center gap-1">
                      {isValid ? (
                        <CheckCircle className="h-3 w-3 text-green-500" />
                      ) : (
                        <AlertCircle className="h-3 w-3 text-gray-400" />
                      )}
                      <span
                        className={`text-[10px] ${
                          isValid ? "text-green-700" : "text-gray-500"
                        }`}
                      >
                        {req.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}
